import React from "react"
import { useEditor } from "@agenteresolve/react"
import { useStyletron } from "baseui"
import { Block } from "baseui/block"
import { Button, SIZE } from "baseui/button"
import { useTranslation } from "react-i18next"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import Scrollable from "~/components/Scrollable"
import { graphics } from "~/constants/mock-data"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"

export default function () {
  const editor = useEditor()
  const { t } = useTranslation("editor")
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const [previews, setPreviews] = React.useState<Record<string, string>>({})

  const addObject = React.useCallback(
    (item: any) => {
      if (editor) {
        editor.objects.add(item)
      }
    },
    [editor]
  )

  // The bundled element previews point at dead CDN URLs. Render each element
  // locally (once, cached in localStorage) so the listing stays coherent.
  React.useEffect(() => {
    if (!editor) return
    let cancelled = false
    const run = async () => {
      const out: Record<string, string> = {}
      for (const graphic of graphics) {
        const key = `element-preview:${graphic.id}`
        let cached: string | null = null
        try {
          cached = localStorage.getItem(key)
        } catch {
          // ignore storage errors
        }
        if (cached) {
          out[graphic.id] = cached
          continue
        }
        try {
          const dataUrl = (await editor.renderer.renderLayer({ ...graphic, left: 0, top: 0 }, {})) as string
          if (dataUrl) {
            out[graphic.id] = dataUrl
            try {
              localStorage.setItem(key, dataUrl)
            } catch {
              // ignore storage errors
            }
          }
        } catch (err) {
          // keep the original preview as fallback
        }
      }
      if (!cancelled && Object.keys(out).length) setPreviews(out)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [editor])

  return (
    <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Block
        $style={{
          display: "flex",
          alignItems: "center",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "1.5rem",
        }}
      >
        <Block>{t("panels.elements.title")}</Block>

        <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
          <AngleDoubleLeft size={18} />
        </Block>
      </Block>
      <Scrollable>
        {/* <Block padding={"0 1.5rem"}>
          <Button
            size={SIZE.compact}
            overrides={{
              Root: {
                style: {
                  width: "100%",
                },
              },
            }}
          >
            {t("panels.elements.computer")}
          </Button>
        </Block> */}
        <Block>
          <Block $style={{ display: "grid", gap: "8px", padding: "1.5rem", gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
            {graphics.map((graphic, index) => (
              <ImageItem
                onClick={() => addObject(graphic)}
                key={index}
                preview={previews[graphic.id] || graphic.preview}
              />
            ))}
          </Block>
        </Block>
      </Scrollable>
    </Block>
  )
}

function ImageItem({ preview, onClick }: { preview: any; onClick?: (option: any) => void }) {
  const [css] = useStyletron()
  return (
    <div
      onClick={onClick}
      className={css({
        position: "relative",
        background: "#f8f8fb",
        cursor: "pointer",
        borderRadius: "8px",
        overflow: "hidden",
        ":hover": {
          opacity: 1,
          background: "rgb(233,233,233)",
        },
      })}
    >
      <img
        src={preview}
        className={css({
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
          verticalAlign: "middle",
        })}
      />
    </div>
  )
}
